import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, BookOpen } from "lucide-react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useSEO } from "@/hooks/useSEO";
import { AdminHeader, ImageUpload, StatusBadge } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = { title: "", category_id: "", excerpt: "", content: "", cover_image_file_id: null, reading_time: 5, tags: [], author: "INVIXA", published: false };

export default function AdminEducation() {
  useSEO("Admin · Education");
  const [articles, setArticles] = useState([]);
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [catName, setCatName] = useState("");

  const load = () => {
    api.get("/admin/education/articles").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/education/categories").then((r) => setCats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openEdit = async (a) => {
    const { data } = await api.get(`/admin/education/articles/${a.id}`);
    setForm({ ...empty, ...data, tags: data.tags || [] }); setEditId(a.id); setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, reading_time: Number(form.reading_time) || 5, category_id: form.category_id || null, tags: typeof form.tags === "string" ? form.tags.split(",").map((t) => t.trim()).filter(Boolean) : form.tags };
    try {
      if (editId) await api.put(`/admin/education/articles/${editId}`, payload);
      else await api.post("/admin/education/articles", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => { await api.delete(`/admin/education/articles/${id}`); toast.success("Deleted"); load(); };
  const addCat = async () => { if (!catName.trim()) return; await api.post("/admin/education/categories", { name: catName, order: 0 }); setCatName(""); toast.success("Category added"); load(); };
  const delCat = async (id) => { await api.delete(`/admin/education/categories/${id}`); load(); };

  return (
    <div>
      <AdminHeader title="Education" subtitle="Manage articles & categories" />
      <Tabs defaultValue="articles">
        <TabsList className="bg-brand-surface border border-white/10">
          <TabsTrigger value="articles" data-testid="tab-articles">Articles</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
        </TabsList>

        <TabsContent value="articles" className="mt-4">
          <div className="flex justify-end mb-4"><Button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} className="gradient-brand text-brand-navy font-semibold" data-testid="new-article-btn"><Plus className="h-4 w-4 mr-1.5" /> New Article</Button></div>
          <div className="space-y-3">
            {articles.length === 0 ? <p className="text-slate-500">No articles yet.</p>
              : articles.map((a) => (
                <div key={a.id} className="card-surface rounded-2xl p-4 flex items-center gap-4" data-testid={`admin-article-${a.id}`}>
                  <div className="h-14 w-20 rounded-lg bg-brand-elevated overflow-hidden shrink-0">{a.cover_image_file_id ? <img src={fileUrl(a.cover_image_file_id)} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full flex items-center justify-center"><BookOpen className="h-5 w-5 text-slate-600" /></div>}</div>
                  <div className="flex-1 min-w-0"><p className="font-display font-semibold text-white truncate">{a.title}</p><p className="text-xs text-slate-500">{a.reading_time} min · {a.author}</p></div>
                  <StatusBadge status={a.published ? "active" : "inactive"} />
                  <button onClick={() => openEdit(a)} className="text-slate-500 hover:text-brand-cyan p-1" data-testid={`edit-article-${a.id}`}><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => del(a.id)} className="text-slate-500 hover:text-red-400 p-1" data-testid={`del-article-${a.id}`}><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
          </div>
        </TabsContent>

        <TabsContent value="categories" className="mt-4">
          <div className="card-surface rounded-2xl p-6 max-w-lg">
            <div className="flex gap-2 mb-4">
              <Input value={catName} onChange={(e) => setCatName(e.target.value)} placeholder="Category name" data-testid="cat-name-input" className="bg-white/5 border-white/10" />
              <Button onClick={addCat} className="gradient-brand text-brand-navy font-semibold" data-testid="add-cat-btn">Add</Button>
            </div>
            <div className="space-y-2">
              {cats.map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-lg bg-white/5 px-4 py-2.5">
                  <span className="text-white text-sm">{c.name}</span>
                  <button onClick={() => delCat(c.id)} className="text-slate-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-brand-surface border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-white">{editId ? "Edit" : "New"} Article</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <ImageUpload value={form.cover_image_file_id} onChange={(v) => setForm({ ...form, cover_image_file_id: v })} label="Cover Image" />
            <div><Label className="text-slate-300">Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="article-title" className="mt-1.5 bg-white/5 border-white/10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-300">Category</Label>
                <Select value={form.category_id || "none"} onValueChange={(v) => setForm({ ...form, category_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="mt-1.5 bg-white/5 border-white/10" data-testid="article-category"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-slate-300">Reading time (min)</Label><Input type="number" value={form.reading_time} onChange={(e) => setForm({ ...form, reading_time: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
            </div>
            <div><Label className="text-slate-300">Excerpt</Label><Textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
            <div><Label className="text-slate-300">Content</Label><Textarea rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="article-content-input" className="mt-1.5 bg-white/5 border-white/10" /></div>
            <div><Label className="text-slate-300">Tags (comma separated)</Label><Input value={Array.isArray(form.tags) ? form.tags.join(", ") : form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} className="mt-1.5 bg-white/5 border-white/10" /></div>
            <label className="flex items-center gap-2 text-sm text-slate-300"><input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} data-testid="article-published" /> Published</label>
            <DialogFooter><Button type="submit" className="gradient-brand text-brand-navy font-semibold" data-testid="article-save">Save</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
